import { AsyncPipe, CommonModule, NgForOf, NgIf } from "@angular/common";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { getHttpErrorMessage } from "../../../shared/utils/http-error.util";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { RouterLink } from "@angular/router";
import { TuiDay, TuiValidationError } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDialog,
  TuiDropdown,
  TuiError,
  TuiLink,
  TuiTextfield,
  TuiLoader
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListWrapper,
  TuiFade,
  TuiFieldErrorPipe,
  TuiFiles,
  tuiFilesAccepted,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiTabs,
  tuiValidationErrorsProvider,
} from "@taiga-ui/kit";
import { TuiNavigation } from "@taiga-ui/layout";
import {
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputDateTimeModule,
  TuiInputModule,
  TuiInputPhoneModule,
  TuiMultiSelectModule,
  TuiSelectModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { catchError, map, of } from "rxjs";
import { FileService } from "../../../core/services/file.service";
import { RolesService } from "../../../core/services/roles.service";
import { Breadcrumb } from "../../../model/interface/breadcrumb";
import { NurseService } from "../../../core/services/nurse.service";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";
import { EnterSubmitDirective } from "../../../core/directives/enter-submit.directive";
import {
  nameValidator,
  numericValidator as sharedNumericValidator,
  phoneValidator,
} from "../../../shared/validators/form-validators";
import { RecommendedHintComponent } from "../../../shared/components/recommended-hint/recommended-hint.component";

@Component({
  selector: "app-add-nurse",
  imports: [
    DisableUnlessPermissionDirective,
    EnterSubmitDirective,
    FormsModule,
    CommonModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiFade,
    TuiLink,
    TuiLoader,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiFiles,
    TuiFieldErrorPipe,
    TuiError,
    ReactiveFormsModule,
    AsyncPipe,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiTextareaModule,
    TuiInputPhoneModule,
    TuiComboBoxModule,
    TuiMultiSelectModule,
    TuiSelectModule,
    TuiDataListWrapper,
    TuiSwitch,
    RouterLink,
    TuiInputDateModule,
    RecommendedHintComponent,
    TuiDialog,
  ],
  templateUrl: "./add-nurse.component.html",
  styleUrl: "./add-nurse.component.scss",
  providers: [
    tuiValidationErrorsProvider({
      duplicateEmail: "Already existing!",
      duplicate: "Already existing!",
      pattern: "Must be exactly 5 digits.",
      required: "Required",
    }),
  ],
})
export class AddNurseComponent {
  breadcrumbs: Breadcrumb[] = [
    { label: "Royal Hospital" },
    { label: "Nurses", link: "admin/nurse" },
    { label: "Add New Nurse" },
  ];
  protected value = false;
  nurseId: string | null = null;
  imageEditable: boolean = false;
  nurseForm: FormGroup;
  isLoading: boolean = false;
  newNurse: boolean = true;
  image: string | null = null;
  roles: any[] = [];
  dropDown: FormControl;
  imagePreview: string | ArrayBuffer | null = null;
  roosterOptions: string[] = ["12 Hours", "24 Hours"];
  lastCasualId: string | null = null;
  suggestedCasualId: string | null = null;
  /** Fixed prefix shown in the UI; only digits are editable. */
  readonly casualIdPrefix = "CID-";
  stringify = (role: any) => role.name;
  maxDate: TuiDay = TuiDay.fromLocalNativeDate(new Date());

  constructor(
    private fb: FormBuilder,
    private nurseService: NurseService,
    private router: Router,
    private alerts: TuiAlertService,
    private route: ActivatedRoute,
    private filesService: FileService,
    private rolesService: RolesService
  ) {
    this.dropDown = this.fb.control(null);
    this.nurseForm = this.fb.group({
      _id: [null],
      name: [
        "",
        [Validators.required, Validators.maxLength(70), nameValidator],
      ],
      // Digits only; composed to CID-##### on save.
      casualIdDigits: [
        "",
        [Validators.required, Validators.pattern(/^\d{5}$/)],
      ],
      email: ["", [Validators.required, Validators.email]],
      // Age is recommended; phone is required (it's the contact channel) but
      // allowed to use real-world formats: +94, spaces, dashes, slashes.
      age: [null, [sharedNumericValidator]],
      contactNo: ["", [Validators.required, phoneValidator]],
      active: [true],
      address: [""],
      notes: [""],
      // Profile photo is recommended (backend defaults to "default.jpg") so
      // we no longer block save on it; the upload UI still works.
      profilePic: [""],
      gurdianName: ["", [Validators.required]],
      gurdianContact: ["", [Validators.required, phoneValidator]],
      registrationDate: [null, Validators.required],
      // DOB is recommended (matches backend, which is required:false).
      dateOfBirth: [null],
      epfNumber: [""],
      appointmentDate: [""],
      employmentType: ["Casual"],
      permanentConversionStatus: ["None"],
      // refNo: [""],
    });
  }

  showPermanentFields = false;
  /** True once the nurse already has a saved Appointment Date (field is locked). */
  appointmentDateLocked = false;
  confirmAppointmentDialogOpen = false;
  pendingAppointmentDateLabel = "";

  navigate(link?: string): void {
    if (link) {
      this.router.navigate([link]);
    }
  }

  getProfilePicError() {
    const control = this.nurseForm.get("profilePic");
    if (control?.hasError("required")) return "Profile Picture is required.";
    return null;
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.nurseId = params.get("id");
      if (this.nurseId) {
        this.isLoading = true;
        this.loadUserData(this.nurseId);
        this.nurseForm.get("email")?.disable();
        this.newNurse = false;
        this.imageEditable = true;
        this.breadcrumbs = [
          { label: "Royal Hospital" },
          { label: "Nurses", link: "admin/nurse" },
          { label: "Edit Nurse" },
        ];
      } else {
        this.loadCasualIdSuggestion();
      }
    });
    this.loadRoles();
  }

  private loadCasualIdSuggestion(autoFill = true): void {
    this.nurseService.getNurseCasualId().subscribe({
      next: (res: any) => {
        const data = res?.data;
        this.lastCasualId = data?.lastId ?? null;
        this.suggestedCasualId = data?.nextId ?? null;

        // Auto-fill the casual id digits for new nurses if the field is empty.
        if (!autoFill || !this.newNurse || !this.suggestedCasualId) return;
        const ctrl = this.nurseForm.get("casualIdDigits");
        if (ctrl && !ctrl.value) {
          ctrl.setValue(this.extractCasualIdDigits(this.suggestedCasualId));
        }
      },
      error: () => {
        this.lastCasualId = null;
        this.suggestedCasualId = null;
      },
    });
  }

  useSuggestedCasualId(): void {
    if (!this.suggestedCasualId) return;
    this.nurseForm
      .get("casualIdDigits")
      ?.setValue(this.extractCasualIdDigits(this.suggestedCasualId));
  }

  /** Extract 5-digit body from CID-##### or NURSE-#####. */
  private extractCasualIdDigits(value: string | null | undefined): string {
    const match = String(value || "").trim().match(/^(?:CID|NURSE)-(\d{5})$/i);
    return match?.[1] ?? "";
  }

  private composeCasualId(digits: string | null | undefined): string | null {
    const d = String(digits || "").trim();
    if (!/^\d{5}$/.test(d)) return null;
    return `${this.casualIdPrefix}${d}`;
  }

  editImage() {
    this.imageEditable = false;
  }

  loadUserData(nurseId: string): void {
    this.isLoading = true;
    this.nurseService
      .getOneNurseById(nurseId)
      .pipe(
        catchError((error) => {
          console.error("Error loading Nurse data:", error);
          this.isLoading = false;
          return of(null);
        })
      )
      .subscribe(
        (
          response: { profilePic?: string; registrationDate?: TuiDay; dateOfBirth?: TuiDay } | null
        ) => {
          if (response) {
            // Convert the registration date if necessary
            if (response.registrationDate) {
              response.registrationDate = TuiDay.fromLocalNativeDate(
                new Date(response.registrationDate.toString())
              );
            }
            if (response.dateOfBirth) {
              response.dateOfBirth = TuiDay.fromLocalNativeDate(
                new Date(response.dateOfBirth.toString())
              );
            }
            const anyRes = response as any;
            if (anyRes.appointmentDate) {
              anyRes.appointmentDate = TuiDay.fromLocalNativeDate(
                new Date(anyRes.appointmentDate.toString())
              );
              this.appointmentDateLocked = true;
            } else {
              this.appointmentDateLocked = false;
            }
            this.showPermanentFields =
              anyRes.permanentConversionStatus === "Approved" ||
              anyRes.employmentType === "Permanent";
            if (this.showPermanentFields) {
              // Casual ID optional once permanent path
              this.nurseForm.get("casualIdDigits")?.clearValidators();
              this.nurseForm.get("casualIdDigits")?.updateValueAndValidity();
            }
            const digits = this.extractCasualIdDigits(anyRes.casualId);
            this.nurseForm.patchValue({
              ...response,
              casualIdDigits: digits,
            });
            if (this.appointmentDateLocked) {
              this.nurseForm.get("appointmentDate")?.disable({ emitEvent: false });
            }
            this.imagePreview = response.profilePic ?? null;
          }
          this.isLoading = false;
        }
      );
  }

  loadRoles(): void {
    this.rolesService.getRolesForOptions().subscribe(
      (response) => {
        this.roles = response as any;
      },
      (error) => {
        console.error("Error fetching roles:", error);
      }
    );
  }

  saveUser() {
    this.isLoading = true;
    if (this.nurseForm.valid) {
      // First-time Appointment Date: confirm before saving
      if (
        this.nurseId &&
        this.showPermanentFields &&
        !this.appointmentDateLocked &&
        this.nurseForm.get("appointmentDate")?.value
      ) {
        this.pendingAppointmentDateLabel = this.formatAppointmentDateLabel(
          this.nurseForm.get("appointmentDate")?.value
        );
        this.isLoading = false;
        this.confirmAppointmentDialogOpen = true;
        return;
      }
      this.persistNurse();
    } else {
      this.nurseForm.markAllAsTouched();
      this.alerts
        .open("Please fill all the required fields", {
          label: "Error",
          appearance: "negative",
        })
        .subscribe();
      this.isLoading = false;
    }
  }

  onAppointmentDateCancel(): void {
    this.confirmAppointmentDialogOpen = false;
    this.isLoading = false;
  }

  onAppointmentDateConfirm(): void {
    this.confirmAppointmentDialogOpen = false;
    this.isLoading = true;
    this.persistNurse();
  }

  private formatAppointmentDateLabel(value: TuiDay | Date | string | null): string {
    if (!value) return "—";
    let date: Date;
    if (value instanceof TuiDay) {
      date = value.toLocalNativeDate();
    } else {
      date = new Date(value as any);
    }
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  private persistNurse(): void {
    const payload = this.nurseForm.getRawValue();
    if (payload.age === "" || payload.age === null) {
      payload.age = null;
    }
    // Sparse unique in MongoDB: never send "" — omit/null so server can $unset
    if (!String(payload.epfNumber ?? "").trim()) {
      payload.epfNumber = null;
    } else {
      payload.epfNumber = String(payload.epfNumber).trim();
    }
    const composed = this.composeCasualId(payload.casualIdDigits);
    delete payload.casualIdDigits;
    if (this.showPermanentFields) {
      payload.casualId = composed || null;
    } else {
      payload.casualId = composed;
    }
    if (this.nurseId) {
      this.nurseService
        .updateNurse(payload)
        .pipe(
          catchError((error) => {
            if (getHttpErrorMessage(error) === "Casual ID is already existing!") {
              this.nurseForm
                .get("casualIdDigits")
                ?.setErrors({ duplicate: true });
            }
            if (getHttpErrorMessage(error) === "Email is already existing!") {
              this.nurseForm
                .get("email")
                ?.setErrors({ duplicateEmail: true });
            }
            if (
              getHttpErrorMessage(error) === "Contact number is already existing!"
            ) {
              this.nurseForm
                .get("contactNo")
                ?.setErrors({ duplicateEmail: true });
            }
            if (
              getHttpErrorMessage(error) ===
              "Client Contact number is already existing!"
            ) {
              this.nurseForm
                .get("gurdianContact")
                ?.setErrors({ duplicateEmail: true });
            }
            this.isLoading = false;
            this.alerts
              .open(getHttpErrorMessage(error), {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
            return of(null);
          })
        )
        .subscribe((res: any) => {
          this.isLoading = false;
          if (res) {
            this.alerts
              .open("Nurse updated successfully", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.router.navigateByUrl(`admin/nurse/${this.nurseId}`);
          }
        });
    } else {
      this.nurseService
        .createNurse(payload)
        .pipe(
          catchError((error) => {
            if (getHttpErrorMessage(error) === "Casual ID is already existing!") {
              this.nurseForm
                .get("casualIdDigits")
                ?.setErrors({ duplicate: true });
            }
            if (getHttpErrorMessage(error) === "Email is already existing!") {
              this.nurseForm
                .get("email")
                ?.setErrors({ duplicateEmail: true });
            }
            if (
              getHttpErrorMessage(error) === "Contact number is already existing!"
            ) {
              this.nurseForm
                .get("contactNo")
                ?.setErrors({ duplicateEmail: true });
            }
            if (
              getHttpErrorMessage(error) ===
              "Client Contact number is already existing!"
            ) {
              this.nurseForm
                .get("gurdianContact")
                ?.setErrors({ duplicateEmail: true });
            }
            this.isLoading = false;
            this.alerts
              .open(getHttpErrorMessage(error), {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
            return of(null);
          })
        )
        .subscribe((res: any) => {
          this.isLoading = false;
          if (res) {
            this.alerts
              .open("Nurse added successfully", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.router.navigateByUrl("admin/nurse");
          }
        });
    }
  }

  // File Upload
  private generatePreview(file: File): void {
    this.imagePreview = URL.createObjectURL(file);
  }

  protected readonly control = new FormControl<File[]>([], [maxFilesLength(1)]);
  protected readonly accepted$ = this.control.valueChanges.pipe(
    map((files) => {
      let acceptedFiles = tuiFilesAccepted(this.control);

      if (acceptedFiles.length > 1) {
        acceptedFiles = [acceptedFiles[acceptedFiles.length - 1]];
        this.control.setValue(acceptedFiles, { emitEvent: false });
      }

      if (acceptedFiles.length > 0) {
        this.uploadImage(acceptedFiles[0]);
      }
      return acceptedFiles;
    })
  );

  protected rejected: readonly File[] = [];

  protected onReject(files: readonly File[]): void {
    this.rejected = Array.from(new Set(this.rejected.concat(files)));
  }

  protected onRemove(file: File): void {
    this.rejected = this.rejected.filter((rejected) => rejected !== file);
    const updatedFiles =
      this.control.value?.filter((current) => current !== file) ?? [];
    this.control.setValue(updatedFiles);
    if (updatedFiles.length === 0) {
      this.imagePreview = null;
    }
  }

  private uploadImage(file: File): void {
    this.generatePreview(file);

    this.isLoading = true;
    this.filesService.postFile(file, "USER_IMAGE_UPLOAD").subscribe(
      (res) => {
        this.isLoading = false;
        const data = res as any;
        this.image = data.url;
        this.nurseForm.get("profilePic")?.patchValue(this.image);
        this.filesService.fileUploadingSubject.next({
          type: "USER_IMAGE_UPLOAD",
          loading: false,
        });
      },
      (error) => {
        this.isLoading = false;
        this.alerts
          .open(getHttpErrorMessage(error), {
            label: "Error",
            appearance: "negative",
          })
          .subscribe();
      }
    );
  }
}

export function maxFilesLength(maxLength: number): ValidatorFn {
  return ({ value }: AbstractControl) =>
    value.length > maxLength
      ? {
        maxLength: new TuiValidationError(
          "Error: maximum limit - 1 file for upload"
        ),
      }
      : null;
}
